import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Send, 
  Wallet, 
  ArrowRight, 
  AlertCircle, 
  CheckCircle, 
  Shield, 
  RefreshCw, 
  Clock,
  LogOut, 
  LogIn,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { 
  checkBalance,
  transferInterMethod,
  requiresMakerChecker 
} from '../../services/transferService';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

interface InterOperatorForm {
  from_method: string;
  from_action: 'deposit' | 'withdrawal';
  to_method: string;
  to_action: 'deposit' | 'withdrawal';
  currency: string;
  amount: string;
  note: string;
}

interface BalanceInfo {
  depositBalance: number;
  withdrawalBalance: number;
  availableForDeposit: number;
  availableForWithdrawal: number;
}

const InterOperatorTransfer: React.FC = () => {
  const { countryConfig } = useLocalization();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [sourceBalances, setSourceBalances] = useState<BalanceInfo>({
    depositBalance: 0,
    withdrawalBalance: 0,
    availableForDeposit: 0,
    availableForWithdrawal: 0
  });
  const [targetBalances, setTargetBalances] = useState<BalanceInfo>({
    depositBalance: 0,
    withdrawalBalance: 0,
    availableForDeposit: 0,
    availableForWithdrawal: 0
  });
  const [loadingBalances, setLoadingBalances] = useState(false);

  const getOperatorsForCountry = () => {
    const operatorColors: { [key: string]: string } = {
      'mpesa': '#E60000',
      'orange': '#FF8C00',
      'airtel': '#E53E3E',
      'afrimoney': '#6B46C1',
    };

    return ['mpesa', 'orange', 'airtel', 'afrimoney'].map(operator => ({
      id: operator,
      name: operator === 'mpesa' ? 'M-Pesa' : operator.charAt(0).toUpperCase() + operator.slice(1),
      color: operatorColors[operator] || '#6B46C1'
    }));
  };

  const operators = getOperatorsForCountry();
  const currencies = countryConfig.currencies;

  const [formData, setFormData] = useState<InterOperatorForm>({
    from_method: 'mpesa',
    from_action: 'withdrawal',
    to_method: 'orange',
    to_action: 'deposit',
    currency: countryConfig.primaryCurrency,
    amount: '',
    note: 'Inter-operator fund transfer'
  });

  const availableTargetOperators = operators.filter(op => op.id !== formData.from_method);

  // S'assurer que target_method est valide
  useEffect(() => {
    if (formData.from_method === formData.to_method) {
      const newTarget = availableTargetOperators[0];
      if (newTarget) {
        setFormData(prev => ({
          ...prev,
          to_method: newTarget.id
        }));
      }
    }
  }, [formData.from_method, availableTargetOperators]);

  // Charger les soldes des deux opérateurs
  const loadBalances = useCallback(async () => {
    if (!user?.merchant_code) return;
    
    try {
      setLoadingBalances(true);
      
      const [sourcePromise, targetPromise] = [
        checkBalance(formData.from_method, formData.currency),
        checkBalance(formData.to_method, formData.currency)
      ];

      const [sourceResult, targetResult] = await Promise.all([sourcePromise, targetPromise]);

      setSourceBalances({
        depositBalance: parseFloat(sourceResult.realtime_deposit),
        withdrawalBalance: parseFloat(sourceResult.realtime_withdrawal),
        availableForDeposit: parseFloat(sourceResult.realtime_deposit),
        availableForWithdrawal: parseFloat(sourceResult.available_for_withdrawal)
      });

      setTargetBalances({
        depositBalance: parseFloat(targetResult.realtime_deposit),
        withdrawalBalance: parseFloat(targetResult.realtime_withdrawal),
        availableForDeposit: parseFloat(targetResult.realtime_deposit),
        availableForWithdrawal: parseFloat(targetResult.available_for_withdrawal)
      });

    } catch (error) {
      console.error('Error loading balances:', error);
    } finally {
      setLoadingBalances(false);
    }
  }, [user?.merchant_code, formData.from_method, formData.to_method, formData.currency]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  const getWalletLabel = (action: 'deposit' | 'withdrawal') => {
    return action === 'deposit' ? 'Pay-in Wallet' : 'Pay-out Wallet';
  };

  const getWalletIcon = (action: 'deposit' | 'withdrawal') => {
    return action === 'deposit' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'PENDING': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'FAILED': return <AlertCircle className="h-4 w-4 text-red-600" />;
      default: return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (!user?.merchant_code) {
      setMessage({ type: 'error', text: 'Merchant code not available' });
      setLoading(false);
      return;
    }

    // Validation
    if (formData.from_method === formData.to_method) {
      setMessage({ type: 'error', text: 'Please select different operators for source and target' });
      setLoading(false);
      return;
    }

    const amount = parseFloat(formData.amount);
    if (amount <= 0) {
      setMessage({ type: 'error', text: 'Amount must be greater than 0' });
      setLoading(false);
      return;
    }

    // Vérifier le solde source
    const sourceBalance = formData.from_action === 'deposit' 
      ? sourceBalances.availableForDeposit 
      : sourceBalances.availableForWithdrawal;

    if (amount > sourceBalance) {
      setMessage({ 
        type: 'error', 
        text: `Insufficient balance. Available: ${sourceBalance.toFixed(2)} ${formData.currency}` 
      });
      setLoading(false);
      return;
    }

    try {
      // Préparer les données de transfert
      const transferData = {
        from_method: formData.from_method,
        to_method: formData.to_method,
        currency: formData.currency,
        amount: amount,
        from_action: formData.from_action,
        to_action: formData.to_action,
        note: formData.note
      };

      const result = await transferInterMethod(transferData);

      // Recharger les soldes
      await loadBalances();
      
      // Afficher le message selon le statut
      if (result.status === 'PENDING') {
        MySwal.fire({
          title: '⏳ Transfer Pending Approval',
          html: `
            <div style="text-align: left;">
              <p style="color: #d97706; font-weight: 600; margin-bottom: 1rem;">
                Transfer submitted for approval
              </p>
              <div style="background-color: #fffbeb; padding: 0.75rem; border-radius: 0.5rem; margin-bottom: 1rem;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; font-size: 0.875rem;">
                  <div style="color: #4b5563;">Reference:</div>
                  <div style="font-weight: 600; color: #111827;">${result.reference}</div>
                  
                  <div style="color: #4b5563;">Amount:</div>
                  <div style="font-weight: bold; color: #d97706;">${result.amount} ${result.currency}</div>
                  
                  <div style="color: #4b5563;">From:</div>
                  <div>${getWalletLabel(formData.from_action)} (${formData.from_method})</div>
                  
                  <div style="color: #4b5563;">To:</div>
                  <div>${getWalletLabel(formData.to_action)} (${formData.to_method})</div>
                  
                  <div style="color: #4b5563;">Status:</div>
                  <div><span style="background-color: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem;">Pending Approval</span></div>
                </div>
              </div>
              <p style="font-size: 0.875rem; color: #92400e; background-color: #fef3c7; padding: 0.75rem; border-radius: 0.5rem;">
                ⏳ ${result.message || 'Transfer pending approval from backoffice. You will be notified once processed.'}
              </p>
              <p style="font-size: 0.75rem; color: #6b7280; margin-top: 0.5rem;">
                Created at: ${new Date(result.created_at).toLocaleString()}
              </p>
            </div>
          `,
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#F59E0B',
          width: '500px',
          customClass: {
            popup: 'rounded-2xl',
            confirmButton: 'px-6 py-2 rounded-lg'
          }
        });

        setMessage({ 
          type: 'info', 
          text: result.message || 'Transfer pending approval from backoffice. You will be notified once processed.' 
        });
      } else {
        MySwal.fire({
          title: '✅ Transfer Successful!',
          html: `
            <div style="text-align: left;">
              <p style="color: #166534; font-weight: 600; margin-bottom: 1rem;">
                Transfer completed successfully!
              </p>
              <div style="background-color: #f0fdf4; padding: 0.75rem; border-radius: 0.5rem; margin-bottom: 1rem;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; font-size: 0.875rem;">
                  <div style="color: #4b5563;">Reference:</div>
                  <div style="font-weight: 600; color: #111827;">${result.reference}</div>
                  
                  <div style="color: #4b5563;">Amount:</div>
                  <div style="font-weight: bold; color: #16a34a;">${result.amount} ${result.currency}</div>
                  
                  <div style="color: #4b5563;">From:</div>
                  <div>${getWalletLabel(formData.from_action)} (${formData.from_method})</div>
                  
                  <div style="color: #4b5563;">To:</div>
                  <div>${getWalletLabel(formData.to_action)} (${formData.to_method})</div>
                  
                  <div style="color: #4b5563;">Status:</div>
                  <div><span style="background-color: #d1fae5; color: #065f46; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem;">Completed</span></div>
                </div>
              </div>
              <p style="font-size: 0.75rem; color: #6b7280; margin-top: 0.5rem;">
                Processed at: ${new Date(result.created_at).toLocaleString()}
              </p>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'Close',
          confirmButtonColor: '#4F46E5',
          width: '500px',
          customClass: {
            popup: 'rounded-2xl',
            confirmButton: 'px-6 py-2 rounded-lg'
          }
        });

        setMessage({ 
          type: 'success', 
          text: result.message || `Transfer completed successfully! Reference: ${result.reference}` 
        });
      }
      
      // Reset form
      setFormData(prev => ({
        ...prev,
        amount: '',
        note: 'Inter-operator fund transfer'
      }));

    } catch (error: any) {
      console.error('Transfer error:', error);
      
      let errorMessage = 'Error processing transfer';
      
      if (error.response?.data) {
        if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'object') {
            errorMessage = error.response.data.detail.message || JSON.stringify(error.response.data.detail);
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      MySwal.fire({
        title: '❌ Transfer Failed',
        html: `
          <div style="text-align: left;">
            <p style="color: #dc2626; font-weight: 600; margin-bottom: 1rem;">
              Transfer could not be processed
            </p>
            <div style="background-color: #fef2f2; padding: 0.75rem; border-radius: 0.5rem;">
              <p style="color: #991b1b; font-size: 0.875rem;">${errorMessage}</p>
            </div>
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#E53E3E',
        width: '500px',
        customClass: {
          popup: 'rounded-2xl',
          confirmButton: 'px-6 py-2 rounded-lg'
        }
      });
      
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
    } finally {
      setLoading(false);
    }
  };

  const sourceOperator = operators.find(op => op.id === formData.from_method);
  const targetOperator = operators.find(op => op.id === formData.to_method);
  const transferAmount = parseFloat(formData.amount || '0');
  const needsApproval = false;

  return (
    <div className="relative max-w-4xl mx-auto p-4">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4">
            <div className="flex flex-col items-center">
              <div className="relative w-20 h-20 mb-6">
                <div className="absolute inset-0 border-4 border-purple-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
                <RefreshCw className="absolute inset-1/4 w-10 h-10 text-purple-600 animate-pulse" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Processing Transfer
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Transferring {transferAmount} {formData.currency}...
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 w-full mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center mr-2 text-white"
                      style={{ backgroundColor: sourceOperator?.color }}
                    >
                      {getWalletIcon(formData.from_action)}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700">
                        {getWalletLabel(formData.from_action)}
                      </p>
                      <p className="text-xs text-gray-500">{sourceOperator?.name}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 mx-2" />
                  <div className="flex items-center">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center mr-2 text-white"
                      style={{ backgroundColor: targetOperator?.color }}
                    >
                      {getWalletIcon(formData.to_action)}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700">
                        {getWalletLabel(formData.to_action)}
                      </p>
                      <p className="text-xs text-gray-500">{targetOperator?.name}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 h-2 rounded-full animate-pulse"></div>
              </div>
              <p className="text-xs text-gray-500">
                Processing transfer between operators...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 mb-6 text-white">
        <div className="flex items-center">
          <div className="bg-white bg-opacity-20 p-3 rounded-lg mr-4">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold mb-1">Inter-Operator Transfer</h1>
            <p className="text-white text-opacity-90 text-sm">
              Transfer funds between different operators
            </p>
          </div>
        </div>
      </div>

      {/* Alert Message */}
      {message && !loading && (
        <div className={`p-3 rounded-lg mb-4 flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : message.type === 'info'
            ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          ) : message.type === 'info' ? (
            <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Source Operator */}
        <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Source Operator</h3>
              <p className="text-xs text-gray-500">{sourceOperator?.name}</p>
            </div>
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white"
              style={{ backgroundColor: sourceOperator?.color }}
            >
              {getWalletIcon(formData.from_action)}
            </div>
          </div>
          {loadingBalances ? (
            <div className="h-10 flex items-center">
              <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ) : (
            <div>
              <div className="mb-2">
                <p className="text-xs text-gray-500">Pay-in Wallet</p>
                <p className="text-lg font-bold text-gray-900">
                  {sourceBalances.depositBalance.toLocaleString()} {formData.currency}
                </p>
                <p className="text-xs text-green-600 font-medium">
                  Available: {sourceBalances.availableForDeposit.toLocaleString()} {formData.currency}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Pay-out Wallet</p>
                <p className="text-lg font-bold text-gray-900">
                  {sourceBalances.withdrawalBalance.toLocaleString()} {formData.currency}
                </p>
                <p className="text-xs text-green-600 font-medium">
                  Available: {sourceBalances.availableForWithdrawal.toLocaleString()} {formData.currency}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Target Operator */}
        <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Target Operator</h3>
              <p className="text-xs text-gray-500">{targetOperator?.name}</p>
            </div>
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white"
              style={{ backgroundColor: targetOperator?.color }}
            >
              {getWalletIcon(formData.to_action)}
            </div>
          </div>
          {loadingBalances ? (
            <div className="h-10 flex items-center">
              <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ) : (
            <div>
              <div className="mb-2">
                <p className="text-xs text-gray-500">Pay-in Wallet</p>
                <p className="text-lg font-bold text-gray-900">
                  {targetBalances.depositBalance.toLocaleString()} {formData.currency}
                </p>
                <p className="text-xs text-green-600 font-medium">
                  Available: {targetBalances.availableForDeposit.toLocaleString()} {formData.currency}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Pay-out Wallet</p>
                <p className="text-lg font-bold text-gray-900">
                  {targetBalances.withdrawalBalance.toLocaleString()} {formData.currency}
                </p>
                <p className="text-xs text-green-600 font-medium">
                  Available: {targetBalances.availableForWithdrawal.toLocaleString()} {formData.currency}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`space-y-6 ${loading ? 'opacity-50' : ''}`}>
        {/* Transfer Configuration */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Wallet className="h-4 w-4 mr-2 text-purple-600" />
            Transfer Configuration
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Operator
              </label>
              <select
                value={formData.from_method}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  from_method: e.target.value,
                  to_method: e.target.value === prev.to_method 
                    ? availableTargetOperators.find(op => op.id !== e.target.value)?.id || 'orange'
                    : prev.to_method
                }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                required
                disabled={loading}
              >
                {operators.map(operator => (
                  <option key={operator.id} value={operator.id}>
                    {operator.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Wallet Type
              </label>
              <select
                value={formData.from_action}
                onChange={(e) => setFormData({...formData, from_action: e.target.value as 'deposit' | 'withdrawal'})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                required
                disabled={loading}
              >
                <option value="deposit">Pay-in Wallet</option>
                <option value="withdrawal">Pay-out Wallet</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Operator
              </label>
              <select
                value={formData.to_method}
                onChange={(e) => setFormData({...formData, to_method: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                required
                disabled={loading}
              >
                {availableTargetOperators.map(operator => (
                  <option key={operator.id} value={operator.id}>
                    {operator.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Wallet Type
              </label>
              <select
                value={formData.to_action}
                onChange={(e) => setFormData({...formData, to_action: e.target.value as 'deposit' | 'withdrawal'})}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                required
                disabled={loading}
              >
                <option value="deposit">Pay-in Wallet</option>
                <option value="withdrawal">Pay-out Wallet</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({...formData, currency: e.target.value})}
              className="w-full md:w-1/4 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              required
              disabled={loading}
            >
              {currencies.map(currency => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Transfer Details */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Send className="h-4 w-4 mr-2 text-purple-600" />
            Transfer Details
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transfer Amount
              </label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Available in {getWalletLabel(formData.from_action)}: {
                  formData.from_action === 'deposit' 
                    ? sourceBalances.availableForDeposit.toLocaleString() 
                    : sourceBalances.availableForWithdrawal.toLocaleString()
                } {formData.currency}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (Optional)
              </label>
              <input
                type="text"
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Transfer purpose or notes"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Transfer Summary */}
        {transferAmount > 0 && !loading && (
          <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
            <h3 className="text-lg font-semibold text-purple-900 mb-4">Transfer Summary</h3>
            
            <div className="flex items-center justify-between mb-4 p-4 bg-white rounded-lg border border-purple-100">
              <div className="flex items-center text-center flex-1">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 text-white"
                  style={{ backgroundColor: sourceOperator?.color }}
                >
                  {getWalletIcon(formData.from_action)}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {getWalletLabel(formData.from_action)}
                  </p>
                  <p className="text-xs text-gray-600">{sourceOperator?.name}</p>
                  <p className="text-xs text-red-600 font-medium">
                    - {transferAmount.toLocaleString()} {formData.currency}
                  </p>
                </div>
              </div>
              
              <ArrowRight className="h-5 w-5 text-purple-600 mx-3 flex-shrink-0" />
              
              <div className="flex items-center text-center flex-1">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 text-white"
                  style={{ backgroundColor: targetOperator?.color }}
                >
                  {getWalletIcon(formData.to_action)}
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {getWalletLabel(formData.to_action)}
                  </p>
                  <p className="text-xs text-gray-600">{targetOperator?.name}</p>
                  <p className="text-xs text-green-600 font-medium">
                    + {transferAmount.toLocaleString()} {formData.currency}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm bg-white rounded-lg p-4 border border-purple-100">
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">Transfer Amount:</span>
                <span className="font-semibold text-gray-900">
                  {formData.amount} {formData.currency}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">Transfer Type:</span>
                <span className="font-medium text-purple-600">
                  {formData.from_method} → {formData.to_method}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">From:</span>
                <span className="font-medium text-blue-600">
                  {getWalletLabel(formData.from_action)}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">To:</span>
                <span className="font-medium text-green-600">
                  {getWalletLabel(formData.to_action)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="font-medium text-purple-900">Status:</span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                  Ready to Process
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <button
            type="submit"
            disabled={loading || !formData.amount || parseFloat(formData.amount) <= 0}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Processing Transfer...
              </div>
            ) : (
              'Initiate Transfer Now'
            )}
          </button>

          <div className="text-center mt-3">
            <p className="text-xs text-gray-500 flex items-center justify-center">
              <Shield className="h-3 w-3 mr-1" />
              Secure transfer between operators • Requires approval for some merchants
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default InterOperatorTransfer;